import React, { useState } from "react";
import { reportsService, commentsService, upvotesService, usersService, storageService } from "@/services";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Save,
  Award,
  Loader2,
  Camera,
  AlertCircle,
  CreditCard
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import SubscriptionTab from "@/components/SubscriptionTab";

export default function Profile() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile'); // 'profile' or 'subscription'
  
  // Use the new AuthContext hook instead of Base44
  const { user, isLoadingAuth: isLoading } = useAuth();

  const [formData, setFormData] = useState({
    full_name: "",
    phone: "",
    neighborhood: "",
    user_role: "resident",
    profile_photo: "",
  });

  React.useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || "",
        phone: user.phone || "",
        neighborhood: user.neighborhood || "",
        user_role: user.user_role || "resident",
        profile_photo: user.profile_photo || "",
      });
    }
  }, [user]);

  const { data: myReports = [] } = useQuery({
    queryKey: ['userReports', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      return await reportsService.filter({ created_by: user.id });
    },
    enabled: !!user,
    initialData: [],
  });

  const { data: myComments = [] } = useQuery({
    queryKey: ['userComments', user?.email],
    queryFn: async () => {
      if (!user?.email) return [];
      return await commentsService.filter({ user_email: user.email });
    },
    enabled: !!user,
    initialData: [],
  });

  const { data: myUpvotes = [] } = useQuery({
    queryKey: ['userUpvotes', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      return await upvotesService.filter({ user_id: user.id });
    },
    enabled: !!user,
    initialData: [],
  });

  const updateProfileMutation = useMutation({
    mutationFn: async (data) => {
      return await usersService.updateMe(data);
    },
    onSuccess: (updatedUser) => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      
      const roleChanged = updatedUser && updatedUser.user_role !== user.user_role;
      
      if (roleChanged) {
        toast.success("🔄 Profile updated! Your dashboard has been changed for your new role.", {
          duration: 4000,
        });
        
        setTimeout(() => {
          if (updatedUser.user_role === 'city_worker') {
            navigate(createPageUrl("CityDashboard"));
          } else if (updatedUser.user_role === 'contractor') {
            navigate(createPageUrl("ContractorJobs"));
          } else if (updatedUser.user_role === 'estate_manager') {
            navigate(createPageUrl("EstateDashboard"));
          } else {
            navigate(createPageUrl("MapView"));
          }
        }, 500);
      } else {
        toast.success("✅ Profile updated successfully!", { duration: 2000 });
      }
      
      setIsEditing(false);
    },
    onError: (error) => {
      toast.error("Failed to update profile. Please check your connection and try again.");
      console.error(error);
    },
  });

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const { file_url } = await storageService.uploadFile(file, 'profile-photos');
      setFormData(prev => ({ ...prev, profile_photo: file_url }));
      toast.success("📸 Profile photo uploaded!", { duration: 2000 });
    } catch (error) {
      toast.error("Failed to upload photo. Please try again.");
      console.error(error);
    }
    setUploading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.full_name.trim()) {
      toast.error("Please enter your full name");
      return;
    }
    
    updateProfileMutation.mutate(formData);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0F0F0] dark:bg-gray-900">
        <Loader2 className="w-12 h-12 animate-spin" style={{ color: '#1883FF' }} />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0F0F0] dark:bg-gray-900">
        <Card className="p-8 text-center">
          <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-4">Please Login</h2>
          <Button onClick={() => navigate('/Login')}>
            Login to View Profile
          </Button>
        </Card>
      </div>
    );
  }

  const resolvedReports = myReports.filter(r => r.status === 'resolved').length;
  const contributionScore = myReports.length * 10 + myComments.length * 2 + myUpvotes.length;

  return (
    <div className="min-h-screen p-4 md:p-8 bg-[#F0F0F0] dark:bg-gray-900">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">My Profile</h1>
          <p className="text-gray-600 dark:text-gray-300">Manage your account and view your community impact</p>
        </div>

        {/* Tab Navigation */}
        {user?.user_role === 'estate_manager' && (
          <motion.div className="mb-6 flex gap-2 flex-wrap bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-2 rounded-xl shadow-sm">
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex items-center gap-2 px-4 py-2 rounded-[24px] md:rounded-[40px] transition-all ${
                activeTab === 'profile'
                  ? 'bg-[#1883FF] text-white shadow-md'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              <User className="w-4 h-4" />
              <span className="font-medium">Profile Info</span>
            </button>
            <button
              onClick={() => setActiveTab('subscription')}
              className={`flex items-center gap-2 px-4 py-2 rounded-[24px] md:rounded-[40px] transition-all ${
                activeTab === 'subscription'
                  ? 'bg-[#1883FF] text-white shadow-md'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              <CreditCard className="w-4 h-4" />
              <span className="font-medium">Subscription</span>
            </button>
          </motion.div>
        )}

        {activeTab === 'profile' && (
        <div className="grid lg:grid-cols-3 gap-6">{/* Profile Card */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm">
              <div className="flex items-start justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Profile Information</h2>
                {!isEditing ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                  >
                    Edit Profile
                  </Button>
                ) : null}
              </div>

              <form onSubmit={handleSubmit}>
                {/* Photo Upload */}
                <div className="flex items-center gap-6 mb-6">
                  <div className="relative">
                    <Avatar className="w-24 h-24 border-4 border-emerald-200 dark:border-emerald-700">
                      <AvatarImage src={formData.profile_photo} />
                      <AvatarFallback className="bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300 text-2xl">
                        {formData.full_name?.[0] || user.email?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    {isEditing && (
                      <label className="absolute bottom-0 right-0 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full p-2 cursor-pointer shadow-lg transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          className="hidden"
                          disabled={uploading}
                        />
                        {uploading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Camera className="w-4 h-4" />
                        )}
                      </label>
                    )}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                      {formData.full_name || user.email}
                    </h3>
                    <Badge className="bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300 capitalize mt-2">
                      {formData.user_role?.replace('_', ' ')}
                    </Badge>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Full Name */}
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="full_name"
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        disabled={!isEditing}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  {/* Email (Read-only) */}
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="email"
                        value={user.email}
                        disabled
                        className="pl-10 bg-gray-50 dark:bg-gray-700"
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        disabled={!isEditing}
                        className="pl-10"
                        placeholder="+27 123 456 789"
                      />
                    </div>
                  </div>

                  {/* Neighborhood */}
                  <div className="space-y-2">
                    <Label htmlFor="neighborhood">Neighborhood</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="neighborhood"
                        value={formData.neighborhood}
                        onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                        disabled={!isEditing}
                        className="pl-10"
                        placeholder="e.g., Woodstock"
                      />
                    </div>
                  </div>
                </div>

                {isEditing && (
                  <div className="flex gap-3 mt-6">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsEditing(false);
                        setFormData({
                          full_name: user.full_name || "",
                          phone: user.phone || "",
                          neighborhood: user.neighborhood || "",
                          user_role: user.user_role || "resident",
                          profile_photo: user.profile_photo || "",
                        });
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={updateProfileMutation.isPending}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {updateProfileMutation.isPending ? "Saving..." : "Save Changes"}
                    </Button>
                  </div>
                )}
              </form>
            </Card>
          </div>

          {/* Stats Sidebar */}
          <div className="space-y-6">
            {!['estate_manager', 'contractor'].includes(user?.user_role) && (
              <>
                {/* Community Impact */}
                <Card className="p-6 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-gray-800 border-emerald-200 dark:border-emerald-800">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-emerald-600 flex items-center justify-center">
                      <Award className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 dark:text-white">Community Impact</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Your contribution score</p>
                    </div>
                  </div>
                  <div className="text-center py-4">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", duration: 0.5 }}
                    >
                      <p className="text-5xl font-bold text-emerald-600 dark:text-emerald-400">{contributionScore}</p>
                    </motion.div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Points</p>
                  </div>
                </Card>

                {/* Activity Stats */}
                <Card className="p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm">
                  <h3 className="font-bold mb-4 text-gray-900 dark:text-white">Activity Summary</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <span className="text-sm text-gray-700 dark:text-gray-200">Reports Submitted</span>
                      <span className="font-bold text-blue-700 dark:text-blue-400">{myReports.length}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <span className="text-sm text-gray-700 dark:text-gray-200">Issues Resolved</span>
                      <span className="font-bold text-green-700 dark:text-green-400">{resolvedReports}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <span className="text-sm text-gray-700 dark:text-gray-200">Comments Posted</span>
                      <span className="font-bold text-purple-700 dark:text-purple-400">{myComments.length}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                      <span className="text-sm text-gray-700 dark:text-gray-200">Issues Upvoted</span>
                      <span className="font-bold text-amber-700 dark:text-amber-400">{myUpvotes.length}</span>
                    </div>
                  </div>
                </Card>
              </>
            )}

            {/* Quick Actions */}
            <Card className="p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm">
              <h3 className="font-bold mb-4 text-gray-900 dark:text-white">Quick Actions</h3>
              <div className="space-y-2">
                {!['estate_manager', 'contractor'].includes(user?.user_role) && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => navigate(createPageUrl("MyActivity"))}
                  >
                    View My Activity
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate(createPageUrl("Settings"))}
                >
                  Settings
                </Button>
              </div>
            </Card>
          </div>
        </div>
        )}

        {/* Subscription Tab */}
        {activeTab === 'subscription' && user?.user_role === 'estate_manager' && (
          <SubscriptionTab />
        )}
      </div>
    </div>
  );
}